import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Select,
  Input,
  Icon,
  Button,
  useToast,
  Heading,
  FormControl,
  FormLabel,
  HStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdPlayArrow, MdSave, MdAssessment, MdClass } from 'react-icons/md';
import httpClient from 'utils/httpClient';
import { recordBulkAttendance, getByCourse } from 'services/course-attendance.service';

const ATT_STATUSES = ['PRESENT', 'ABSENT', 'LATE', 'JUSTIFIED'];

const statusLabel = {
  PRESENT: 'Presente',
  ABSENT: 'Ausente',
  LATE: 'Tarde',
  JUSTIFIED: 'Justificado',
};

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function pctColor(pct) {
  if (pct > 80) return 'green';
  if (pct >= 60 && pct <= 80) return 'yellow';
  return 'red';
}

export default function CourseAttendanceView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(todayISO());
  const [roster, setRoster] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [sessionReady, setSessionReady] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const [summaryCourseId, setSummaryCourseId] = useState('');
  const [summaryRows, setSummaryRows] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const toast = useToast();

  const loadCourses = useCallback(async () => {
    try {
      setLoadingCourses(true);
      const res = await httpClient.get('/dictated-courses');
      const raw = res?.payload;
      const list = Array.isArray(raw) ? raw : raw?.courses || raw?.data || [];
      setCourses(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setCourses([]);
      toast({ title: 'Error', description: 'No se pudieron cargar los cursos', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoadingCourses(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const courseLabel = (c) => {
    const subj = c.subject?.description || c.subject?.name || '';
    return subj ? `${subj} (#${c.id})` : `Curso #${c.id}`;
  };

  const handlePasarLista = async () => {
    if (!courseId || !attendanceDate) {
      toast({ title: 'Seleccione curso y fecha', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    const cid = Number(courseId);
    setLoadingList(true);
    try {
      const swc = await httpClient.get('/course-discounts/students-with-courses');
      const studentsPayload = swc?.payload;
      const allStudents = Array.isArray(studentsPayload) ? studentsPayload : [];
      const enrolled = allStudents.filter((s) => (s.courses || []).some((co) => Number(co.id) === cid));

      const existing = await getByCourse(cid, { date: attendanceDate });
      const rows = existing?.payload?.data || [];
      const byStudent = {};
      rows.forEach((r) => {
        byStudent[r.student_id] = r.status;
      });

      const map = {};
      enrolled.forEach((s) => {
        const sid = s.id;
        map[sid] = byStudent[sid] || 'PRESENT';
      });

      setRoster(enrolled);
      setAttendanceMap(map);
      setSessionReady(true);
      if (enrolled.length === 0) {
        toast({ title: 'Sin estudiantes inscriptos', description: 'No hay alumnos en este curso.', status: 'info', duration: 4000, isClosable: true });
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'No se pudo cargar la lista', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoadingList(false);
    }
  };

  const setStatus = (studentId, status) => {
    setAttendanceMap((m) => ({ ...m, [studentId]: status }));
  };

  const handleGuardar = async () => {
    if (!courseId || !attendanceDate || !sessionReady) return;
    const cid = Number(courseId);
    // Conceptualmente se cargan INASISTENCIAS. La presencia se asume por
    // defecto y no se persiste en DB para no llenar la tabla.
    const records = roster
      .map((s) => ({
        studentId: s.id,
        status: attendanceMap[s.id] || 'PRESENT',
      }))
      .filter((r) => r.status !== 'PRESENT');
    if (records.length === 0) {
      toast({
        title: 'Sin inasistencias para guardar',
        description: 'Todos quedaron como presentes (no se persiste nada).',
        status: 'info',
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      await recordBulkAttendance({
        courseId: cid,
        attendanceDate,
        records,
        createdBy: 1,
      });
      toast({ title: 'Inasistencias guardadas', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo guardar', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const loadSummaryByCourse = async () => {
    if (!summaryCourseId) {
      toast({ title: 'Seleccione un curso', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setLoadingSummary(true);
    try {
      const res = await getByCourse(Number(summaryCourseId), {});
      const data = res?.payload?.data || [];
      const byId = {};
      data.forEach((row) => {
        const sid = row.student_id;
        const name = row.student_name || `Estudiante ${sid}`;
        if (!byId[sid]) {
          byId[sid] = {
            studentId: sid,
            name,
            total: 0,
            present: 0,
            absent: 0,
            late: 0,
            justified: 0,
          };
        }
        const agg = byId[sid];
        agg.total += 1;
        if (row.status === 'PRESENT') agg.present += 1;
        else if (row.status === 'ABSENT') agg.absent += 1;
        else if (row.status === 'LATE') agg.late += 1;
        else if (row.status === 'JUSTIFIED') agg.justified += 1;
      });
      const rowsOut = Object.values(byId).map((r) => {
        const counted = r.present + r.late + r.justified;
        const pct = r.total > 0 ? Math.round((counted / r.total) * 10000) / 100 : 0;
        return { ...r, pct };
      });
      rowsOut.sort((a, b) => a.name.localeCompare(b.name));
      setSummaryRows(rowsOut);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudo cargar el resumen', status: 'error', duration: 4000, isClosable: true });
      setSummaryRows([]);
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (courseId && !summaryCourseId) setSummaryCourseId(courseId);
  }, [courseId, summaryCourseId]);

  const selectedCourse = useMemo(() => courses.find((c) => String(c.id) === String(courseId)), [courses, courseId]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }} mb={6}>
        <Flex px="25px" justify="space-between" mb="20px" align="center" flexWrap="wrap" gap={4}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Inasistencias por curso
          </Text>
        </Flex>
        <Flex px="25px" mb="20px" gap={4} flexWrap="wrap" align="flex-end">
          <FormControl maxW="320px">
            <FormLabel fontSize="sm">Curso</FormLabel>
            <Select
              placeholder={loadingCourses ? 'Cargando...' : 'Seleccionar curso'}
              value={courseId}
              onChange={(e) => {
                setCourseId(e.target.value);
                setSessionReady(false);
                setRoster([]);
                setAttendanceMap({});
              }}
              isDisabled={loadingCourses}
            >
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {courseLabel(c)}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Fecha</FormLabel>
            <Input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
          </FormControl>
          <Button
            leftIcon={<MdPlayArrow />}
            colorScheme="brand"
            onClick={handlePasarLista}
          >
            Pasar Lista
          </Button>
          <Button leftIcon={<MdSave />} colorScheme="green" variant="solid" onClick={handleGuardar} isDisabled={!sessionReady || roster.length === 0}>
            Guardar inasistencias
          </Button>
        </Flex>

        {selectedCourse && sessionReady && (
          <Text px="25px" mb={2} fontSize="sm" color="gray.500">
            {courseLabel(selectedCourse)} · {new Date(attendanceDate + 'T12:00:00').toLocaleDateString('es-UY')}
          </Text>
        )}

        {!loadingList && sessionReady && roster.length > 0 ? (
          <Box overflowX="auto" px="25px" pb={6}>
            <Table variant="simple" color="gray.500" size="sm">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Estudiante</Th>
                  <Th borderColor={borderColor}>Estado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {roster.map((s) => {
                  const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.document || `ID ${s.id}`;
                  return (
                    <Tr key={s.id} _hover={{ bg: rowHover }}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="600" fontSize="sm" color={textColor}>
                          {name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {s.document}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Select
                          size="sm"
                          maxW="220px"
                          value={attendanceMap[s.id] || 'PRESENT'}
                          onChange={(e) => setStatus(s.id, e.target.value)}
                        >
                          {ATT_STATUSES.map((st) => (
                            <option key={st} value={st}>
                              {statusLabel[st]}
                            </option>
                          ))}
                        </Select>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        ) : sessionReady ? (
          <Text px="25px" pb={6} color="gray.500" fontSize="sm">
            No hay estudiantes inscriptos en este curso.
          </Text>
        ) : null}
      </Card>

      <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" justify="space-between" mb="20px" align="center" flexWrap="wrap" gap={4}>
          <HStack>
            <Icon as={MdAssessment} />
            <Heading size="md" color={textColor}>
              Resumen de asistencia por estudiante
            </Heading>
          </HStack>
        </Flex>
        <Flex px="25px" mb="20px" gap={4} flexWrap="wrap" align="flex-end">
          <FormControl maxW="320px">
            <FormLabel fontSize="sm">Curso (resumen)</FormLabel>
            <Select placeholder="Seleccionar curso" value={summaryCourseId} onChange={(e) => setSummaryCourseId(e.target.value)}>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {courseLabel(c)}
                </option>
              ))}
            </Select>
          </FormControl>
          <Button leftIcon={<MdClass />} variant="outline" colorScheme="brand" onClick={loadSummaryByCourse}>
            Cargar resumen del curso
          </Button>
        </Flex>

        {!loadingSummary && (
          <Box overflowX="auto" px="25px" pb={6}>
            <Table variant="simple" color="gray.500" size="sm" mb={4}>
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Nombre</Th>
                  <Th borderColor={borderColor} isNumeric>
                    Total clases
                  </Th>
                  <Th borderColor={borderColor} isNumeric>
                    Presente
                  </Th>
                  <Th borderColor={borderColor} isNumeric>
                    Ausente
                  </Th>
                  <Th borderColor={borderColor} isNumeric>
                    Tarde
                  </Th>
                  <Th borderColor={borderColor}>% Asistencia</Th>
                </Tr>
              </Thead>
              <Tbody>
                {summaryRows.map((r) => (
                  <Tr key={r.studentId}>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="600" fontSize="sm">
                        {r.name}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      {r.total}
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      {r.present}
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      {r.absent}
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      {r.late}
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge colorScheme={pctColor(r.pct)} fontSize="xs" borderRadius="6px" px={2}>
                        {r.pct}%
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {!loadingSummary && summaryRows.length === 0 && (
              <Text color="gray.500" fontSize="sm" textAlign="center" py={6}>
                Pulse &quot;Cargar resumen del curso&quot; para ver estadísticas acumuladas.
              </Text>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
}
